import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  Alert,
  Paper,
  CircularProgress,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Chip,
  Stack,
  Tooltip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton
} from '@mui/material';
import {
  Flag as FlagIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Gavel as GavelIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiService } from '../services/api';

interface UserReport {
  _id: string;
  deviceId?: string;
  userId?: string;
  userEmail?: string;
  category: string;
  description?: string;
  reportedContentType?: string;
  reportedContentId?: string;
  reportedContentUrl?: string;
  status: 'pending' | 'reviewed' | 'actioned' | 'dismissed';
  reviewedBy?: { name: string; email: string };
  reviewedAt?: string;
  actionNotes?: string;
  createdAt: string;
}

interface ReportStats {
  pending: number;
  reviewed: number;
  actioned: number;
  dismissed: number;
  total: number;
}

const CATEGORY_LABELS: Record<string, string> = {
  sexual_content: 'Sexual content',
  minor_child_safety: 'Minor / child safety',
  non_consensual: 'Non-consensual',
  hate_speech: 'Hate speech',
  violence: 'Violence',
  other: 'Other',
};

const STATUS_COLORS: Record<string, 'default' | 'warning' | 'info' | 'success' | 'error'> = {
  pending: 'warning',
  reviewed: 'info',
  actioned: 'error',
  dismissed: 'default',
};

const formatRelative = (iso?: string) => (iso ? new Date(iso).toLocaleString() : '—');

const UserReportsSection: React.FC = () => {
  const queryClient = useQueryClient();
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [actionDialog, setActionDialog] = useState<{
    open: boolean;
    report: UserReport | null;
    newStatus: UserReport['status'];
    notes: string;
  }>({ open: false, report: null, newStatus: 'reviewed', notes: '' });

  const { data: stats } = useQuery({
    queryKey: ['userReportStats'],
    queryFn: async () => {
      const r = await apiService.getUserReportStats();
      return r.data as ReportStats;
    }
  });

  const { data: reportsResp, isLoading } = useQuery({
    queryKey: ['userReports', statusFilter],
    queryFn: async () => {
      const r = await apiService.getUserReports({
        status: statusFilter || undefined,
        limit: 100
      });
      return r.data as { items: UserReport[]; total: number };
    }
  });

  const updateMutation = useMutation({
    mutationFn: async (payload: {
      id: string;
      status: string;
      actionNotes: string;
    }) =>
      apiService.updateUserReportStatus(payload.id, {
        status: payload.status,
        actionNotes: payload.actionNotes
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['userReports'] });
      queryClient.invalidateQueries({ queryKey: ['userReportStats'] });
      setActionDialog({ open: false, report: null, newStatus: 'reviewed', notes: '' });
    }
  });

  // Delete a user-submitted report
  const [deleteDialog, setDeleteDialog] = useState<{ open: boolean; report: UserReport | null }>({
    open: false,
    report: null
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => apiService.deleteUserReport(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['userReports'] });
      queryClient.invalidateQueries({ queryKey: ['userReportStats'] });
      setDeleteDialog({ open: false, report: null });
    }
  });

  const openAction = (report: UserReport, newStatus: UserReport['status']) => {
    setActionDialog({
      open: true,
      report,
      newStatus,
      notes: report.actionNotes ?? ''
    });
  };

  return (
    <Card>
      <CardContent>
        <Box display="flex" alignItems="center" gap={1} mb={1}>
          <FlagIcon color="error" />
          <Typography variant="h6">User-submitted Reports</Typography>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Content flagged by end users via the in-app Report button. Used as
          part of our human-oversight moderation workflow.
        </Typography>

        {/* Stats */}
        <Grid container spacing={1.5} sx={{ mb: 2 }}>
          {[
            { label: 'Pending', value: stats?.pending, color: '#ffa726' },
            { label: 'Reviewed', value: stats?.reviewed, color: '#42a5f5' },
            { label: 'Actioned', value: stats?.actioned, color: '#ef5350' },
            { label: 'Dismissed', value: stats?.dismissed, color: '#9e9e9e' },
            { label: 'Total', value: stats?.total, color: '#66bb6a' },
          ].map((s) => (
            <Grid item xs={6} sm={2.4} key={s.label}>
              <Paper variant="outlined" sx={{ p: 1.5 }}>
                <Typography variant="caption" color="text.secondary">{s.label}</Typography>
                <Typography variant="h6" fontWeight="bold" sx={{ color: s.color }}>
                  {s.value ?? 0}
                </Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>

        {/* Filter */}
        <Box display="flex" justifyContent="flex-end" mb={1.5}>
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Filter status</InputLabel>
            <Select
              value={statusFilter}
              label="Filter status"
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="pending">Pending</MenuItem>
              <MenuItem value="reviewed">Reviewed</MenuItem>
              <MenuItem value="actioned">Actioned</MenuItem>
              <MenuItem value="dismissed">Dismissed</MenuItem>
            </Select>
          </FormControl>
        </Box>

        {isLoading ? (
          <Box display="flex" justifyContent="center" p={4}>
            <CircularProgress />
          </Box>
        ) : (reportsResp?.items || []).length === 0 ? (
          <Alert severity="success">No user reports {statusFilter ? `with status "${statusFilter}"` : 'yet'}.</Alert>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Submitted</TableCell>
                <TableCell>Category</TableCell>
                <TableCell>Reporter</TableCell>
                <TableCell>Description</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(reportsResp?.items || []).map((r) => (
                <TableRow key={r._id} hover>
                  <TableCell>
                    <Typography variant="body2">{formatRelative(r.createdAt)}</Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={CATEGORY_LABELS[r.category] || r.category}
                      size="small"
                      color={r.category === 'minor_child_safety' ? 'error' : 'default'}
                      variant={r.category === 'minor_child_safety' ? 'filled' : 'outlined'}
                    />
                  </TableCell>
                  <TableCell>
                    <Stack spacing={0.3}>
                      {r.userEmail && (
                        <Typography variant="body2">{r.userEmail}</Typography>
                      )}
                      {r.deviceId && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ fontFamily: 'monospace' }}
                        >
                          {r.deviceId.slice(0, 24)}{r.deviceId.length > 24 ? '…' : ''}
                        </Typography>
                      )}
                    </Stack>
                  </TableCell>
                  <TableCell sx={{ maxWidth: 280 }}>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                      {r.description || <em style={{ color: '#888' }}>—</em>}
                    </Typography>
                    {r.reportedContentUrl && (
                      <Typography
                        component="a"
                        href={r.reportedContentUrl}
                        target="_blank"
                        rel="noreferrer"
                        variant="caption"
                        color="primary"
                        sx={{ wordBreak: 'break-all' }}
                      >
                        view content
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={r.status}
                      size="small"
                      color={STATUS_COLORS[r.status]}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                      {r.status === 'pending' && (
                        <Tooltip title="Mark reviewed">
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<CheckCircleIcon />}
                            onClick={() => openAction(r, 'reviewed')}
                          >
                            Reviewed
                          </Button>
                        </Tooltip>
                      )}
                      {r.status !== 'actioned' && r.status !== 'dismissed' && (
                        <Tooltip title="Take action (e.g. block offender)">
                          <Button
                            size="small"
                            color="error"
                            variant="contained"
                            startIcon={<GavelIcon />}
                            onClick={() => openAction(r, 'actioned')}
                          >
                            Action
                          </Button>
                        </Tooltip>
                      )}
                      {r.status === 'pending' && (
                        <Tooltip title="Dismiss as not actionable">
                          <Button
                            size="small"
                            variant="outlined"
                            color="inherit"
                            startIcon={<CancelIcon />}
                            onClick={() => openAction(r, 'dismissed')}
                          >
                            Dismiss
                          </Button>
                        </Tooltip>
                      )}
                      <Tooltip title="Delete this report">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => setDeleteDialog({ open: true, report: r })}
                          disabled={deleteMutation.isPending}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>

      {/* Action dialog */}
      <Dialog
        open={actionDialog.open}
        onClose={() => setActionDialog({ ...actionDialog, open: false })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Mark report as <strong>{actionDialog.newStatus}</strong>
        </DialogTitle>
        <DialogContent dividers>
          {actionDialog.report && (
            <Stack spacing={1.5}>
              <Typography variant="body2">
                <strong>Category:</strong>{' '}
                {CATEGORY_LABELS[actionDialog.report.category] || actionDialog.report.category}
              </Typography>
              {actionDialog.report.description && (
                <Typography variant="body2">
                  <strong>Reporter said:</strong> {actionDialog.report.description}
                </Typography>
              )}
              <TextField
                label="Action notes (optional)"
                multiline
                rows={3}
                value={actionDialog.notes}
                onChange={(e) =>
                  setActionDialog({ ...actionDialog, notes: e.target.value })
                }
                helperText="Internal note about what was done with this report (e.g. 'Banned device 3cd0...', 'Template removed', 'No violation found')."
              />
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setActionDialog({ ...actionDialog, open: false })}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color={actionDialog.newStatus === 'actioned' ? 'error' : 'primary'}
            disabled={updateMutation.isPending}
            onClick={() =>
              actionDialog.report &&
              updateMutation.mutate({
                id: actionDialog.report._id,
                status: actionDialog.newStatus,
                actionNotes: actionDialog.notes
              })
            }
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete report confirmation */}
      <Dialog
        open={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false, report: null })}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Delete this report?</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2">
            The report will be permanently removed from the moderation queue.
            Any block actions you've already applied against the reporter are
            NOT affected.
          </Typography>
          {deleteDialog.report?.description && (
            <Box sx={{ mt: 1.5, p: 1.5, bgcolor: 'background.default', borderRadius: 1 }}>
              <Typography variant="caption" color="text.secondary">
                Reporter said:
              </Typography>
              <Typography variant="body2">{deleteDialog.report.description}</Typography>
            </Box>
          )}
          <Alert severity="warning" sx={{ mt: 2 }}>This cannot be undone.</Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, report: null })}>
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            startIcon={<DeleteIcon />}
            disabled={deleteMutation.isPending}
            onClick={() => deleteDialog.report && deleteMutation.mutate(deleteDialog.report._id)}
          >
            Delete report
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
};

export default UserReportsSection;
